import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { CalendarCheck, Plus, Star, Pencil, X, Video, Phone, Building2, MapPin, Clock, ChevronDown } from "lucide-react";
import { recruitmentAPI } from "../services/api";

const STATUS_COLORS = { scheduled: "#5e6ad2", completed: "#16a34a", cancelled: "#dc2626" };
const TYPE_ICONS = { video: Video, phone: Phone, onsite: Building2 };

const S = {
  surface: { backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" },
  input: {
    backgroundColor: "var(--lm-s1)",
    border: "1px solid var(--lm-border)",
    color: "var(--lm-ink)",
  },
};

export default function EntretiensPlanning() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [evalTarget, setEvalTarget] = useState(null);

  const { data: interviews = [], isLoading } = useQuery({
    queryKey: ["recruitment-interviews", statusFilter],
    queryFn: () =>
      recruitmentAPI.listInterviews(statusFilter ? { status: statusFilter } : {}).then((r) => r.data),
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: ["recruitment-interviews"] });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h3 className="font-semibold text-base" style={{ color: "var(--lm-ink)" }}>
            {t("entretiens.title")}
          </h3>
          <p className="text-xs mt-0.5" style={{ color: "var(--lm-ink-3)" }}>
            {t("entretiens.count", { count: interviews.length })}
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium"
          style={{ backgroundColor: "#5e6ad2", color: "#fff", border: "1px solid #5e6ad2" }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#828fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#5e6ad2"; }}
        >
          <Plus className="h-4 w-4" />
          {t("entretiens.plan")}
        </button>
      </div>

      {/* Status filter */}
      <div className="flex gap-2 flex-wrap">
        {[
          { value: "", label: t("entretiens.filterAll") },
          { value: "scheduled", label: t("entretiens.status.scheduled") },
          { value: "completed", label: t("entretiens.status.completed") },
          { value: "cancelled", label: t("entretiens.status.cancelled") },
        ].map((f) => (
          <button
            key={f.value}
            onClick={() => setStatusFilter(f.value)}
            className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
            style={{
              backgroundColor: statusFilter === f.value ? "#5e6ad2" : "var(--lm-s2)",
              color: statusFilter === f.value ? "#fff" : "var(--lm-ink-2)",
              border: `1px solid ${statusFilter === f.value ? "#5e6ad2" : "var(--lm-border)"}`,
            }}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* List */}
      {isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-xl animate-pulse" style={{ backgroundColor: "var(--lm-s2)" }} />
          ))}
        </div>
      ) : interviews.length === 0 ? (
        <EmptyState onPlan={() => setShowCreate(true)} />
      ) : (
        <div className="space-y-2">
          {interviews.map((iv) => (
            <InterviewRow
              key={iv.id}
              iv={iv}
              onEdit={() => setEditTarget(iv)}
              onEvaluate={() => setEvalTarget(iv)}
              onCancel={() => cancelInterview(iv.id, qc, invalidate, t("entretiens.cancelConfirm"))}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreate && (
        <CreateInterviewModal
          onClose={() => setShowCreate(false)}
          onSaved={invalidate}
        />
      )}
      {editTarget && (
        <EditInterviewModal
          interview={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={invalidate}
        />
      )}
      {evalTarget && (
        <EvaluateModal
          interview={evalTarget}
          onClose={() => setEvalTarget(null)}
          onSaved={invalidate}
        />
      )}
    </div>
  );
}

async function cancelInterview(id, qc, invalidate, confirmMsg) {
  if (!window.confirm(confirmMsg)) return;
  await recruitmentAPI.updateInterview(id, { status: "cancelled" });
  invalidate();
}

// ─────────────────────────────────────────
// Interview row
// ─────────────────────────────────────────
function InterviewRow({ iv, onEdit, onEvaluate, onCancel }) {
  const { t, i18n } = useTranslation();
  const statusColor = STATUS_COLORS[iv.status] || STATUS_COLORS.scheduled;
  const statusLabel = t(`entretiens.status.${iv.status}`, { defaultValue: iv.status });
  const TypeIcon = TYPE_ICONS[iv.type] || TYPE_ICONS.video;
  const typeLabel = t(`entretiens.type.${iv.type}`, { defaultValue: iv.type });
  const isCompleted = iv.status === "completed";
  const isCancelled = iv.status === "cancelled";

  return (
    <div
      className="rounded-xl px-5 py-4 flex items-center gap-5"
      style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
    >
      {/* Date block */}
      <div
        className="flex-shrink-0 w-14 h-14 rounded-lg flex flex-col items-center justify-center text-center"
        style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
      >
        {iv.scheduled_at ? (
          <>
            <span className="text-xs font-medium" style={{ color: "var(--lm-ink-3)" }}>
              {new Date(iv.scheduled_at).toLocaleDateString(i18n.language, { day: "2-digit", month: "short" })}
            </span>
            <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>
              {new Date(iv.scheduled_at).toLocaleTimeString(i18n.language, { hour: "2-digit", minute: "2-digit" })}
            </span>
          </>
        ) : (
          <Clock className="h-5 w-5" style={{ color: "var(--lm-ink-3)" }} />
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>
            {iv.candidate_name || "—"}
          </span>
          <span className="text-xs" style={{ color: "var(--lm-ink-3)" }}>·</span>
          <span className="text-xs truncate" style={{ color: "var(--lm-ink-2)" }}>
            {iv.job_title || "—"}
          </span>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Type badge */}
          <span className="flex items-center gap-1 text-xs" style={{ color: "var(--lm-ink-3)" }}>
            <TypeIcon className="h-3.5 w-3.5" />
            {typeLabel}
          </span>
          {/* Location */}
          {iv.location_or_link && (
            <span className="flex items-center gap-1 text-xs truncate max-w-48" style={{ color: "var(--lm-ink-3)" }}>
              <MapPin className="h-3 w-3 flex-shrink-0" />
              {iv.location_or_link}
            </span>
          )}
          {/* Score */}
          {isCompleted && iv.evaluation_score && (
            <ScorePip score={iv.evaluation_score} />
          )}
        </div>
      </div>

      {/* Status badge */}
      <span
        className="flex-shrink-0 text-xs px-2.5 py-1 rounded-full font-medium"
        style={{ color: statusColor, backgroundColor: `${statusColor}18`, border: `1px solid ${statusColor}30` }}
      >
        {statusLabel}
      </span>

      {/* Actions */}
      {!isCancelled && (
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {!isCompleted && (
            <ActionBtn icon={Pencil} title={t("entretiens.actionEdit")} onClick={onEdit} />
          )}
          {iv.status === "scheduled" && (
            <ActionBtn icon={Star} title={t("entretiens.actionEvaluate")} onClick={onEvaluate} accent />
          )}
          {!isCompleted && (
            <ActionBtn icon={X} title={t("entretiens.actionCancel")} onClick={onCancel} danger />
          )}
        </div>
      )}
    </div>
  );
}

function ActionBtn({ icon: Icon, title, onClick, accent, danger }) {
  const color = danger ? "#dc2626" : accent ? "#5e6ad2" : "var(--lm-ink-3)";
  return (
    <button
      onClick={onClick}
      title={title}
      className="p-1.5 rounded-lg transition-colors"
      style={{ color }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = `${danger ? "rgba(220,38,38,0.1)" : accent ? "rgba(94,106,210,0.1)" : "var(--lm-s2)"}`; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}

function ScorePip({ score }) {
  return (
    <span className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className="h-3 w-3"
          style={{ color: s <= score ? "#f59e0b" : "var(--lm-border)", fill: s <= score ? "#f59e0b" : "transparent" }}
        />
      ))}
    </span>
  );
}

// ─────────────────────────────────────────
// Empty state
// ─────────────────────────────────────────
function EmptyState({ onPlan }) {
  const { t } = useTranslation();
  return (
    <div className="py-16 flex flex-col items-center gap-4 text-center">
      <div
        className="w-14 h-14 rounded-full flex items-center justify-center"
        style={{ backgroundColor: "rgba(94,106,210,0.10)", border: "1px solid rgba(94,106,210,0.20)" }}
      >
        <CalendarCheck className="h-6 w-6" style={{ color: "#5e6ad2" }} />
      </div>
      <div className="space-y-1">
        <p className="text-sm font-medium" style={{ color: "var(--lm-ink)" }}>{t("entretiens.emptyTitle")}</p>
        <p className="text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("entretiens.emptyHint")}</p>
      </div>
      <button
        onClick={onPlan}
        className="px-4 py-2 rounded-lg text-sm font-medium"
        style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
      >
        {t("entretiens.emptyCta")}
      </button>
    </div>
  );
}

// ─────────────────────────────────────────
// Create modal
// ─────────────────────────────────────────
function CreateInterviewModal({ onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    application_id: "",
    type: "video",
    scheduled_at: "",
    location_or_link: "",
  });
  const [appSearch, setAppSearch] = useState("");
  const [error, setError] = useState(null);

  const { data: applications = [] } = useQuery({
    queryKey: ["recruitment-applications-for-interview"],
    queryFn: () => recruitmentAPI.listApplications({ limit: 200 }).then((r) => r.data),
  });

  const filtered = applications.filter((a) => {
    if (!appSearch) return true;
    const name = `${a.candidate?.first_name || ""} ${a.candidate?.last_name || ""}`.toLowerCase();
    return name.includes(appSearch.toLowerCase());
  });

  const mut = useMutation({
    mutationFn: (data) => recruitmentAPI.createInterview(data),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (err) => setError(err?.response?.data?.detail || t("entretiens.create.error")),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.application_id) { setError(t("entretiens.create.selectApp")); return; }
    setError(null);
    mut.mutate({
      ...form,
      application_id: parseInt(form.application_id),
      scheduled_at: form.scheduled_at || null,
    });
  };

  return (
    <Modal title={t("entretiens.create.modalTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorBanner msg={error} />}

        {/* Application search */}
        <FieldLabel label={t("entretiens.create.application")}>
          <input
            value={appSearch}
            onChange={(e) => setAppSearch(e.target.value)}
            placeholder={t("entretiens.create.searchPlaceholder")}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={S.input}
          />
          {appSearch && (
            <div
              className="mt-1 rounded-lg overflow-hidden max-h-40 overflow-y-auto"
              style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}
            >
              {filtered.slice(0, 10).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => {
                    setForm((f) => ({ ...f, application_id: String(a.id) }));
                    setAppSearch(`${a.candidate?.first_name} ${a.candidate?.last_name}`);
                  }}
                  className="w-full text-left px-3 py-2 text-xs transition-colors"
                  style={{ color: "var(--lm-ink-2)", borderBottom: "1px solid var(--lm-border)" }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "var(--lm-s1)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "transparent"; }}
                >
                  <span style={{ color: "var(--lm-ink)" }}>{a.candidate?.first_name} {a.candidate?.last_name}</span>
                  {" · "}
                  <span style={{ color: "var(--lm-ink-3)" }}>{a.job_posting?.title || t("entretiens.create.postingFallback", { id: a.job_posting_id })}</span>
                </button>
              ))}
              {filtered.length === 0 && (
                <p className="px-3 py-2 text-xs" style={{ color: "var(--lm-ink-3)" }}>{t("entretiens.create.noResults")}</p>
              )}
            </div>
          )}
        </FieldLabel>

        {/* Type */}
        <FieldLabel label={t("entretiens.create.typeLabel")}>
          <Select value={form.type} onChange={(v) => setForm((f) => ({ ...f, type: v }))}
            options={[
              { value: "video", label: t("entretiens.type.video") },
              { value: "phone", label: t("entretiens.type.phone") },
              { value: "onsite", label: t("entretiens.type.onsite") },
            ]}
          />
        </FieldLabel>

        {/* Date/time */}
        <FieldLabel label={t("entretiens.create.dateLabel")}>
          <input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={S.input}
          />
        </FieldLabel>

        {/* Location */}
        <FieldLabel label={t("entretiens.create.locationLabel")}>
          <input
            value={form.location_or_link}
            onChange={(e) => setForm((f) => ({ ...f, location_or_link: e.target.value }))}
            placeholder={t("entretiens.create.locationPlaceholder")}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={S.input}
          />
        </FieldLabel>

        <ModalActions onClose={onClose} isPending={mut.isPending} submitLabel={t("entretiens.create.submit")} />
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────
// Edit modal
// ─────────────────────────────────────────
function EditInterviewModal({ interview, onClose, onSaved }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    type: interview.type,
    scheduled_at: interview.scheduled_at
      ? new Date(interview.scheduled_at).toISOString().slice(0, 16)
      : "",
    location_or_link: interview.location_or_link || "",
    status: interview.status,
  });
  const [error, setError] = useState(null);

  const mut = useMutation({
    mutationFn: (data) => recruitmentAPI.updateInterview(interview.id, data),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (err) => setError(err?.response?.data?.detail || t("entretiens.edit.error")),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);
    mut.mutate({ ...form, scheduled_at: form.scheduled_at || null });
  };

  return (
    <Modal title={t("entretiens.edit.modalTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && <ErrorBanner msg={error} />}

        <div className="px-4 py-3 rounded-lg text-xs" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
          <span style={{ color: "var(--lm-ink-3)" }}>{t("entretiens.edit.candidateLabel")}</span>
          <span style={{ color: "var(--lm-ink)" }}>{interview.candidate_name || "—"}</span>
          <span style={{ color: "var(--lm-ink-3)" }}> · {interview.job_title || "—"}</span>
        </div>

        <FieldLabel label={t("entretiens.create.typeLabel")}>
          <Select value={form.type} onChange={(v) => setForm((f) => ({ ...f, type: v }))}
            options={[
              { value: "video", label: t("entretiens.type.video") },
              { value: "phone", label: t("entretiens.type.phone") },
              { value: "onsite", label: t("entretiens.type.onsite") },
            ]}
          />
        </FieldLabel>

        <FieldLabel label={t("entretiens.create.dateLabel")}>
          <input
            type="datetime-local"
            value={form.scheduled_at}
            onChange={(e) => setForm((f) => ({ ...f, scheduled_at: e.target.value }))}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={S.input}
          />
        </FieldLabel>

        <FieldLabel label={t("entretiens.create.locationLabel")}>
          <input
            value={form.location_or_link}
            onChange={(e) => setForm((f) => ({ ...f, location_or_link: e.target.value }))}
            placeholder={t("entretiens.create.locationPlaceholder")}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none"
            style={S.input}
          />
        </FieldLabel>

        <FieldLabel label={t("entretiens.edit.statusLabel")}>
          <Select value={form.status} onChange={(v) => setForm((f) => ({ ...f, status: v }))}
            options={[
              { value: "scheduled", label: t("entretiens.status.scheduled") },
              { value: "completed", label: t("entretiens.status.completed") },
              { value: "cancelled", label: t("entretiens.status.cancelled") },
            ]}
          />
        </FieldLabel>

        <ModalActions onClose={onClose} isPending={mut.isPending} submitLabel={t("entretiens.edit.submit")} />
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────
// Evaluate modal
// ─────────────────────────────────────────
function EvaluateModal({ interview, onClose, onSaved }) {
  const { t } = useTranslation();
  const [score, setScore] = useState(interview.evaluation_score || 0);
  const [notes, setNotes] = useState(interview.evaluation_notes || "");
  const [hover, setHover] = useState(0);
  const [error, setError] = useState(null);

  const mut = useMutation({
    mutationFn: (data) => recruitmentAPI.evaluateInterview(interview.id, data),
    onSuccess: () => { onSaved(); onClose(); },
    onError: (err) => setError(err?.response?.data?.detail || t("entretiens.evaluate.error")),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!score) { setError(t("entretiens.evaluate.scoreRequired")); return; }
    setError(null);
    mut.mutate({ evaluation_score: score, evaluation_notes: notes || null });
  };

  const LABELS = { 1: t("entretiens.evaluate.score1"), 2: t("entretiens.evaluate.score2"), 3: t("entretiens.evaluate.score3"), 4: t("entretiens.evaluate.score4"), 5: t("entretiens.evaluate.score5") };

  return (
    <Modal title={t("entretiens.evaluate.modalTitle")} onClose={onClose}>
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && <ErrorBanner msg={error} />}

        <div className="px-4 py-3 rounded-lg text-xs" style={{ backgroundColor: "var(--lm-s2)", border: "1px solid var(--lm-border)" }}>
          <span style={{ color: "var(--lm-ink-3)" }}>{t("entretiens.edit.candidateLabel")}</span>
          <span style={{ color: "var(--lm-ink)" }}>{interview.candidate_name || "—"}</span>
          <span style={{ color: "var(--lm-ink-3)" }}> · {interview.job_title || "—"}</span>
        </div>

        {/* Star rating */}
        <div className="space-y-2">
          <p className="text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{t("entretiens.evaluate.overallScore")}</p>
          <div className="flex items-center gap-3">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => setScore(s)}
                  onMouseEnter={() => setHover(s)}
                  onMouseLeave={() => setHover(0)}
                >
                  <Star
                    className="h-7 w-7 transition-transform hover:scale-110"
                    style={{
                      color: s <= (hover || score) ? "#f59e0b" : "var(--lm-border)",
                      fill: s <= (hover || score) ? "#f59e0b" : "transparent",
                    }}
                  />
                </button>
              ))}
            </div>
            {(hover || score) > 0 && (
              <span className="text-xs font-medium" style={{ color: "#f59e0b" }}>
                {LABELS[hover || score]}
              </span>
            )}
          </div>
        </div>

        {/* Notes */}
        <FieldLabel label={t("entretiens.evaluate.comments")}>
          <textarea
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("entretiens.evaluate.commentsPlaceholder")}
            className="w-full px-3 py-2 rounded-lg text-sm outline-none resize-none"
            style={S.input}
          />
        </FieldLabel>

        <ModalActions onClose={onClose} isPending={mut.isPending} submitLabel={t("entretiens.evaluate.submit")} />
      </form>
    </Modal>
  );
}

// ─────────────────────────────────────────
// Shared UI helpers
// ─────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.6)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl shadow-2xl overflow-y-auto max-h-[90vh]"
        style={{ backgroundColor: "var(--lm-s1)", border: "1px solid var(--lm-border)" }}
      >
        <div className="flex items-center justify-between px-6 py-4" style={{ borderBottom: "1px solid var(--lm-border)" }}>
          <h3 className="font-semibold text-sm" style={{ color: "var(--lm-ink)" }}>{title}</h3>
          <button onClick={onClose} className="p-1 rounded" style={{ color: "var(--lm-ink-3)" }}>
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

function FieldLabel({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium" style={{ color: "var(--lm-ink-2)" }}>{label}</label>
      {children}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 rounded-lg text-sm outline-none appearance-none pr-8"
        style={S.input}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none" style={{ color: "var(--lm-ink-3)" }} />
    </div>
  );
}

function ModalActions({ onClose, isPending, submitLabel }) {
  const { t } = useTranslation();
  return (
    <div className="flex gap-3 pt-2">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-lg text-sm"
        style={{ backgroundColor: "var(--lm-s2)", color: "var(--lm-ink-2)", border: "1px solid var(--lm-border)" }}
      >
        {t("entretiens.modalCancel")}
      </button>
      <button
        type="submit"
        disabled={isPending}
        className="flex-1 px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60"
        style={{ backgroundColor: "#5e6ad2", color: "#fff" }}
      >
        {isPending ? t("entretiens.modalSaving") : submitLabel}
      </button>
    </div>
  );
}

function ErrorBanner({ msg }) {
  return (
    <div
      className="px-4 py-3 rounded-lg text-xs"
      style={{ backgroundColor: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.20)", color: "#dc2626" }}
    >
      {msg}
    </div>
  );
}
